import React from 'react';
import { Box, Chip, Tooltip, Link } from '@mui/material';
import { getPlatformColor, getPlatformName, getPlatformTextColor, platformPostUrl } from '../config/platforms';
import { outcomeText, resultMeta } from '../config/postStatus';

export interface PlatformResult {
  platform: string;
  platform_post_id?: string;
  /** The platform's own URL for the post, where it reports one. Preferred over a derived URL. */
  permalink?: string | null;
  status: string;
  error_message?: string;
  published_at?: string;
  /**
   * Engagement the platform reported, refreshed hourly. Null when we have no figures —
   * which is NOT the same as zero, hence `measured`. The hourly refresh only covers posts
   * published in the last 30 days, so older posts stay unmeasured permanently.
   */
  likes?: number | null;
  comments?: number | null;
  shares?: number | null;
  impressions?: number | null;
  /** Accounts the post reached. Instagram only; null elsewhere. */
  reach?: number | null;
  /**
   * Times the post was seen. Instagram reports this directly; X's "views" is mirrored here
   * from the same figure it reports as `impressions`, so the column means one thing.
   */
  views?: number | null;
  /**
   * Paid engagement, from the ad that boosted this post. **Separate from the organic figures
   * above, never added into them** — a post showing 3 likes organically and 102 from the boost
   * is 105 in the Instagram app, and keeping them apart is what answers "what did the spend
   * buy". NULL on a post that was never boosted, which is not the same as 0.
   */
  paid_likes?: number | null;
  paid_comments?: number | null;
  paid_shares?: number | null;
  paid_impressions?: number | null;
  paid_reach?: number | null;
  paid_clicks?: number | null;
  paid_spend?: number | null;
  metrics_fetched_at?: string | null;
  /** 1 when a metrics row exists for this result, 0 when none does. */
  measured?: number;
  /** 1 when an ad ran against this post. */
  promoted?: number;
}

interface Props {
  platforms: string[];
  results?: PlatformResult[];
  size?: 'small' | 'medium';
  /** Tighter chips for a dense list, such as the dashboard's recent activity. */
  dense?: boolean;
}

/**
 * One chip per targeted platform, showing what actually happened there.
 *
 * Four states, visually distinct, which is the whole point: a filled brand-coloured chip
 * published, an outline in the same brand colour is still to come, an amber outline was
 * rejected before sending, a red outline was refused by the platform.
 *
 * A pending network used to be grey, which read as "nothing to do with this network" — on a
 * dashboard listing scheduled posts that made the whole list colourless, with no way to see
 * where anything was going without reading every label. Published chips link to the live post
 * where the platform gives us a usable URL.
 */
export const PlatformOutcomes: React.FC<Props> = ({ platforms, results = [], size = 'small', dense = false }) => (
  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: dense ? 0.5 : 0.75 }}>
    {platforms.map(platform => {
      // No result row at all means the post has not been published yet
      const result = results.find(r => r.platform === platform);
      const meta = resultMeta(result?.status);
      const url = platformPostUrl(platform, result?.platform_post_id, result?.permalink);
      const published = result?.status === 'success';
      // No result row at all: the post has not reached this network yet. Distinct from the
      // outcomes below, which have all been attempted.
      const pending = !result;
      const brand = getPlatformColor(platform);
      // The border can carry the raw brand colour; the label cannot — several of them are too
      // light to read as text on white.
      const brandText = getPlatformTextColor(platform);

      const borderColor =
        result?.status === 'failed' ? 'error.main'
          : result?.status === 'rejected' ? 'warning.main'
            // A network still to come is drawn in its own colour rather than grey. Grey read
            // as "nothing to do with this network", which on a dashboard of scheduled posts
            // meant the whole list was colourless and you could not tell at a glance where
            // anything was going.
            : pending ? brand
              : 'divider';

      const chip = (
        <Chip
          label={getPlatformName(platform)}
          size={size}
          icon={meta?.icon}
          sx={{
            ...(dense ? { height: 20, fontSize: '0.7rem' } : {}),
            cursor: url ? 'pointer' : 'default',
            bgcolor: published ? brand : 'transparent',
            // Filled brand colour means it went out; the same colour as an outline means it is
            // still to come. The two are never confusable, and neither is grey.
            color: published ? '#fff' : pending ? brandText : 'text.secondary',
            border: published ? 'none' : '1px solid',
            borderColor,
            fontWeight: pending ? 500 : undefined,
            '& .MuiChip-icon': {
              // One rule, or a second '& .MuiChip-icon' key further up would be replaced
              // wholesale by this one rather than merged with it.
              ...(dense ? { fontSize: 13 } : {}),
              color: published
                ? '#fff'
                : pending
                  ? brandText
                  : result?.status === 'rejected'
                    ? 'warning.main'
                    // A removed post is not a failure — it went out and was taken back down
                    : result?.status === 'removed'
                      ? 'text.disabled'
                      : 'error.main',
            },
          }}
        />
      );

      return (
        <Tooltip key={platform} title={outcomeText(getPlatformName(platform), result)} arrow>
          {url ? (
            <Link href={url} target="_blank" rel="noopener noreferrer" underline="none">
              {chip}
            </Link>
          ) : (
            <span>{chip}</span>
          )}
        </Tooltip>
      );
    })}
  </Box>
);
